import { ChangeEvent, useCallback, useRef } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { v4 as uuidv4 } from 'uuid';
import { connect, useDispatch } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IKnmFormAttachment } from 'KnmPackage/MainModule/interfaces/IKnmFormAttachment';
import { UploadFile } from 'UIPackage/MainModule/components/UploadFile';
import { useKnmDocumentAttachment } from 'KnmPackage/MainModule/hooks/useKnmDocumentAttachment';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import {
  IKnmErknmFormViolationEventMaterial,
  IKnmErknmFormViolationEventMaterialLink,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { makeRequired } from 'CorePackage/MainModule/validators/isRequired';
import { REQUIRED_ERROR } from 'CorePackage/MainModule/constants/validationErrors';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { ADD_KNM_DOCUMENT_ERROR, INVALID_TYPE_FILE } from 'CorePackage/MainModule/constants/notices';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import {
  isPhotoTypeDocConfirmationViolation,
  isVideoTypeDocConfirmationViolation,
} from 'KnmErknmPackage/MainModule/common/isTypeDocConfirmationViolationCode';
import { isFileExtensionValid } from 'CorePackage/MainModule/validators/isFileExtensionValid';

import styles from '../../../DecisionPackage/DecisionViolationModule/components/ErknmDecisionViolationFixAttachmentsField.module.scss';
import { getMaxFileSize } from '../../../CorePackage/MainModule/common/getMaxFileSize';
import { addNoticeKnmFileSizeErrorDetailed } from '../../../CorePackage/NotificationModule/common/addNoticeKnmFileSizeErrorSmall';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { KnmSystemTypesEnum } from '../../../KnmPackage/MainModule/enums/KnmSystemTypesEnum';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getAdminFetchSettings } from '../../../PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { getKnmSystemType } from '../../../KnmPackage/MainModule/selectors/knmSelectors';
import { MB } from '../../../CorePackage/MainModule/constants/fileSize';

interface IErknmActViolationEventAttachmentsFieldProps {
  canEdit: boolean;
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  id: string;
  isDecision?: boolean;
  isKnm?: boolean;
  isPm?: boolean;
  material: IKnmErknmFormViolationEventMaterial;
  typeCode?: string;
}

interface IErknmActViolationEventAttachmentsFieldStoreProps {
  knmSystemType: KnmSystemTypesEnum | undefined;
  settings: IAdminSetting[];
}

const ACCEPTED_FILES_EXTENSIONS = ['zip', 'pdf', 'doc', 'docx', 'odt', 'jpg', 'jpeg', 'png', 'xlsx', 'heic', 'webp'];
const ACCEPTED_FILES_FORMATS = ACCEPTED_FILES_EXTENSIONS.map(ext => `.${ext}`).join(',');

const ErknmActViolationEventAttachmentsFieldWithoutStore = ({
  canEdit,
  fieldName,
  formRef,
  id,
  isDecision,
  isKnm,
  isPm,
  material,
  typeCode: typeCodeProp,
  knmSystemType,
  settings,
}: IErknmActViolationEventAttachmentsFieldProps & IErknmActViolationEventAttachmentsFieldStoreProps) => {
  const dispatch = useDispatch();
  const { downloadKnmDocumentAttachment, uploadKnmDocumentAttachment } = useKnmDocumentAttachment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const attachmentsFieldName = `${fieldName}.attachments`;
  const linksFieldName = `${fieldName}.links`;
  const fileFieldName = `${fieldName}.file`;
  const typeCode = typeCodeProp || material.typeDocConfirmationViolation?.code;
  const isVideoLinks = isVideoTypeDocConfirmationViolation(typeCode);
  const hasFiles = isPhotoTypeDocConfirmationViolation(typeCode);
  const maxFileSize = getMaxFileSize(settings, knmSystemType);

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addAttachment = (data: IKnmFormAttachment) => {
    push && push(attachmentsFieldName, data);
  };

  const makeRemoveAttachmentHandler = (index: number) => () => remove && remove(attachmentsFieldName, index);
  const makeRemoveLinkHandler = (index: number) => () => remove && remove(linksFieldName, index);

  const handleAddLink = useCallback(() => {
    const newLink: IKnmErknmFormViolationEventMaterialLink = {
      id: uuidv4(),
      value: null,
    };

    push && push(linksFieldName, newLink);
  }, [linksFieldName, push]);

  const handleFileSizeError = useCallback(() => {
    dispatch(addNoticeKnmFileSizeErrorDetailed(maxFileSize));
  }, [dispatch, maxFileSize]);

  const makeFileInputChangeHandler = () => async (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;

    if (files) {
      const file = files[0];

      if (!isFileExtensionValid(file.name, ACCEPTED_FILES_EXTENSIONS)) {
        dispatch(
          addNoticeAction({
            renderContent: INVALID_TYPE_FILE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.VALIDATE_DOCUMENT_TYPE,
          }),
        );
      } else if (file.size > maxFileSize * MB) {
        handleFileSizeError();
      } else {
        const listId = uuidv4();
        const newAttachment: IKnmFormAttachment = {
          fileName: file.name,
          fileSize: Math.trunc(file.size / 1000) || 0,
          signatureInfo: null,
          isNew: true,
          listId,
          file,
        };

        try {
          const uploadedAttachment = await uploadKnmDocumentAttachment(file);

          addAttachment(newAttachment);

          if (uploadedAttachment) {
            // @ts-ignore
            const currentAttachments = formRef?.current.getFieldState(attachmentsFieldName as keyof IKnmForm)?.value as
              | IKnmFormAttachment[]
              | undefined;
            const uploadedAttachmentIndex = currentAttachments?.findIndex(item => item.listId === listId);

            if (typeof uploadedAttachmentIndex === 'number' && uploadedAttachmentIndex >= 0) {
              const attachmentFieldName = `${attachmentsFieldName}[${uploadedAttachmentIndex}]` as keyof IKnmForm;

              // @ts-ignore
              formRef?.current.change(attachmentFieldName, { ...uploadedAttachment, isNew: true });
            }
          }
        } catch (err) {
          dispatch(
            addNoticeAction({
              renderContent: ADD_KNM_DOCUMENT_ERROR,
              noticeType: NoticeTypesEnum.ERROR,
              messageType: MessageTypesEnum.UPLOAD_DOCUMENT,
            }),
          );
        }
      }
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadAttachment = (downloadingAttachment: IKnmFormAttachment) => () => {
    const { fileName, filePath, fileSize, mimeType } = downloadingAttachment;

    if (filePath && mimeType) {
      downloadKnmDocumentAttachment({ fileName, filePath, fileSize, mimeType });
    }
  };

  if (!typeCode) {
    return null;
  }

  if (isVideoLinks) {
    return (
      <>
        <Row>
          <div id={`${id}.linkAddButton`}>
            <span className="shared-field-label">
              {getTitleFieldWithRedStar({
                label: 'Ссылки на документ',
                field: linksFieldName,
                isPm,
                isKnm,
                isDecision,
              })}
            </span>
            {canEdit && (
              <Button
                className="shared-btn-add"
                text="Добавить ссылку"
                type="button"
                variant="link"
                color="primary"
                onClick={handleAddLink}
              />
            )}
          </div>
        </Row>

        <FieldArray name={linksFieldName}>
          {({ fields, meta }: FieldArrayRenderProps<IKnmErknmFormViolationEventMaterialLink, HTMLElement>) => (
            <>
              {!!fields.length &&
                fields.map((linkFieldName, linkIndex) => (
                  <Row key={linkFieldName} notSpace>
                    <TextInputField
                      id={`${id}.links[${linkIndex}].value`}
                      className="shared-row-flex"
                      name={`${linkFieldName}.value`}
                      placeholder={getTitleFieldWithRedStar({
                        label: 'Введите ссылку',
                        field: `${linkFieldName}.value`,
                        isPm,
                        isKnm,
                        isDecision,
                      })}
                      disabled={!canEdit}
                      validate={makeRequired(REQUIRED_ERROR)}
                    />
                    {canEdit && (
                      <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveLinkHandler(linkIndex)} />
                    )}
                  </Row>
                ))}
              {typeof meta.error === 'string' && meta.touched && (
                <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
              )}
            </>
          )}
        </FieldArray>
      </>
    );
  }

  if (!hasFiles) {
    return null;
  }

  return (
    <>
      <Row>
        <div id={`${id}.fileAddButton`}>
          <span className="shared-field-label">
            {getTitleFieldWithRedStar({
              label: 'Файлы',
              field: attachmentsFieldName,
              isPm,
              isKnm,
              isDecision,
            })}
          </span>
          <input
            ref={fileInputRef}
            id={fileFieldName}
            name={fileFieldName}
            type="file"
            accept={ACCEPTED_FILES_FORMATS}
            style={{ display: 'none' }}
            onChange={makeFileInputChangeHandler()}
          />
          {canEdit && hasFiles && (
            <label className="shared-link" htmlFor={fileFieldName}>
              Добавить
            </label>
          )}
        </div>
      </Row>

      <FieldArray name={attachmentsFieldName}>
        {({ fields, meta }: FieldArrayRenderProps<IKnmFormAttachment, HTMLElement>) => (
          <>
            {!!fields.length && (
              <ul className={styles.AttachmentsList}>
                {fields.map((attachmentFieldName, attachmentIndex) => {
                  const attachment = fields.value?.[attachmentIndex];

                  if (!attachment) {
                    return null;
                  }

                  let progress: number | undefined;

                  if (attachment.isNew) {
                    progress = attachment.dateCreate ? 100 : 0;
                  }

                  return (
                    <li key={`${attachmentFieldName}-${attachmentIndex}`}>
                      <UploadFile
                        fileName={attachment.fileName || ''}
                        fileSize={attachment.fileSize}
                        progress={progress}
                        onFileNameClick={attachment.isNew ? undefined : downloadAttachment(attachment)}
                        onRemove={canEdit ? makeRemoveAttachmentHandler(attachmentIndex) : undefined}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            {typeof meta.error === 'string' && meta.touched && (
              <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
            )}
          </>
        )}
      </FieldArray>
    </>
  );
};

const mapStateToProps = (state: IRootState): IErknmActViolationEventAttachmentsFieldStoreProps => ({
  knmSystemType: getKnmSystemType(state),
  settings: getAdminFetchSettings(state),
});

export const ErknmActViolationEventAttachmentsField = connect(mapStateToProps)(
  ErknmActViolationEventAttachmentsFieldWithoutStore,
);
